import { FC, useState } from 'react';
import { CardBody, HeadingMedium, Pagination, Table, theme } from '@arnold/common';
import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { OrganizationSettingQuery, useGetCreditHistoryQuery } from '../../generated/hooks';
import { Loading } from '../../components';
import { getDataSource, TransactionHistoryData } from './getDataSource';
import { SetCreditsForm } from './SetCreditsForm';

const RECORDS_ON_PAGE = 20;

export const TransactionHistory: FC<{
  productId: string;
  organizationId: string;
  isSysAdmin: boolean;
  productVersion?: NonNullable<NonNullable<OrganizationSettingQuery['organization']>['productVersionHistory']>[number];
  isFreeVersion?: boolean;
}> = ({ productId, organizationId, isSysAdmin, productVersion, isFreeVersion }) => {
  const { t } = useTranslation('settings');
  const [currentPage, setCurrentPage] = useState(1);
  const { data, loading, error } = useGetCreditHistoryQuery({
    variables: { productId },
    fetchPolicy: 'cache-and-network',
  });

  const onChangePage = (page: number) => {
    setCurrentPage(page);
  };

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return null;
  }

  // Copy before sort — Apollo cache arrays are read-only. Newest first for the table.
  const allCreditHistory = data?.getCreditHistory || [];
  const displayHistory = [...allCreditHistory].sort((a, b) => {
    const ta = new Date(a.createdAt || 0).getTime();
    const tb = new Date(b.createdAt || 0).getTime();
    if (ta !== tb) return tb - ta;
    return Number(b.id) - Number(a.id);
  });
  const pageHistory = displayHistory.slice((currentPage - 1) * RECORDS_ON_PAGE, currentPage * RECORDS_ON_PAGE);

  return (
    <Card data-cy="admin-transaction-history-card">
      <CardBody>
        {isFreeVersion && (
          <HeadingMedium margin={`0 0 ${theme.spacing.g} 0`} bold>
            {t('freeVersionCreditHistoryTitle')}
          </HeadingMedium>
        )}
        {isSysAdmin && !isFreeVersion && <SetCreditsForm organizationId={organizationId} />}

        <Table<TransactionHistoryData>
          labels={{ emptyTableLabel: t('emptyTableLabel') }}
          columns={[
            {
              key: 'date',
              title: t('date'),
              dataIndex: 'date',
            },
            {
              key: 'organization',
              title: t('organization'),
              dataIndex: 'organization',
            },
            {
              key: 'type',
              title: t('type'),
              dataIndex: 'type',
            },
            {
              key: 'administrator',
              title: t('administrator'),
              dataIndex: 'administrator',
            },
            {
              key: 'creditsUsed',
              title: t('creditsUsed'),
              dataIndex: 'creditsUsed',
            },
            {
              key: 'creditsBalance',
              title: t('creditsBalance'),
              dataIndex: 'creditsBalance',
            },
          ]}
          dataSources={getDataSource({
            creditHistory: pageHistory,
            allCreditHistory,
            productVersion,
            t,
          })}
        />
        <Pagination
          totalPages={Math.floor(((data?.getCreditHistory.length || 0) - 1) / RECORDS_ON_PAGE) + 1}
          currentPage={Number(currentPage)}
          onPageChange={onChangePage}
        />
      </CardBody>
    </Card>
  );
};
